import {
    Listbox,
    ListboxItem,
    ListboxSection,
} from '../../shared/Listbox/Listbox';
import { TEMPLATE_DISPLAY_NAMES } from '../model/PostTemplates';
import { TEMPLATE_ICONS } from '../model/TemplateIcons';
import { TemplateId } from '@lifeserver/shared';

import styles from './PostTemplateSelector.module.scss';

const TEMPLATE_SECTIONS: Array<{
    section: string;
    templates: Array<{
        id: TemplateId;
        icon: string;
        description: string;
    }>;
}> = [
    {
        section: 'Small',
        templates: [
            {
                id: 'event-calendar-small',
                icon: TEMPLATE_ICONS['event-calendar-small'],
                description: 'Small event entry for calendar feed.',
            },
        ],
    },
    {
        section: 'Medium',
        templates: [
            {
                id: 'article-static-medium',
                icon: TEMPLATE_ICONS['article-static-medium'],
                description: 'Basic post template',
            },
            {
                id: 'article-interactive-medium',
                icon: TEMPLATE_ICONS['article-interactive-medium'],
                description: 'Post with front/back cover.',
            },
            {
                id: 'link-medium',
                icon: TEMPLATE_ICONS['link-medium'],
                description: 'Takes players somewhere in the game.',
            },
            {
                id: 'link-shop-medium',
                icon: TEMPLATE_ICONS['link-shop-medium'],
                description: 'Takes players to the shop.',
            },
            {
                id: 'link-storefront-medium',
                icon: TEMPLATE_ICONS['link-storefront-medium'],
                description: 'Takes players to the TBW App Store Page',
            },
            {
                id: 'video-medium',
                icon: TEMPLATE_ICONS['video-medium'],
                description: 'Supports full screen with audio.',
            },
            {
                id: 'poll-thisorthat-medium',
                icon: TEMPLATE_ICONS['poll-thisorthat-medium'],
                description: 'Post with a this or that poll.',
            },
            {
                id: 'poll-voteslider-medium',
                icon: TEMPLATE_ICONS['poll-voteslider-medium'],
                description: 'Post with a vote slider poll.',
            },
            {
                id: 'poll-multichoice-medium',
                icon: TEMPLATE_ICONS['poll-multichoice-medium'],
                description:
                    'Smaller variant of multiple choice, only support 2 options',
            },
            {
                id: 'event-calendar-medium',
                icon: TEMPLATE_ICONS['event-calendar-medium'],
                description: 'Single event entry for calendar feed.',
            },
        ],
    },
    {
        section: 'Large',
        templates: [
            {
                id: 'article-static-large',
                icon: TEMPLATE_ICONS['article-static-large'],
                description: 'Basic post template',
            },
            {
                id: 'article-interactive-large',
                icon: TEMPLATE_ICONS['article-interactive-large'],
                description: 'Post with front/back cover.',
            },
            {
                id: 'link-large',
                icon: TEMPLATE_ICONS['link-large'],
                description: 'Takes players somewhere in the game.',
            },
            {
                id: 'link-shop-large',
                icon: TEMPLATE_ICONS['link-shop-large'],
                description: 'Takes players to the shop.',
            },
            {
                id: 'link-storefront-large',
                icon: TEMPLATE_ICONS['link-storefront-large'],
                description: 'Takes players to the TBW App Store Page.',
            },
            {
                id: 'video-large',
                icon: TEMPLATE_ICONS['video-large'],
                description: 'Supports full screen with audio.',
            },
            {
                id: 'poll-thisorthat-large',
                icon: TEMPLATE_ICONS['poll-thisorthat-large'],
                description: 'Post with a this or that poll.',
            },
            {
                id: 'poll-voteslider-large',
                icon: TEMPLATE_ICONS['poll-voteslider-large'],
                description: 'Post with a vote slider poll.',
            },
            {
                id: 'tile-puzzle-large',
                icon: TEMPLATE_ICONS['tile-puzzle-large'],
                description: 'A puzzle template used for secrets',
            },
            {
                id: 'poll-multichoice-large',
                icon: TEMPLATE_ICONS['poll-multichoice-large'],
                description: 'Players can vote between 2-4 options',
            },
        ],
    },
];

export interface PostTemplateSelectorProps {
    value?: TemplateId;
    onChange?: (value: TemplateId) => void;
    allowedTemplates?: TemplateId[];
}

export const PostTemplateSelector = ({
    value,
    onChange,
    allowedTemplates,
}: PostTemplateSelectorProps) => {
    return (
        <Listbox
            variant="grid"
            direction="vertical"
            className={styles.listbox}
            selectedValues={value !== undefined ? [value] : []}
            onSelected={(value) => onChange?.(value as TemplateId)}
        >
            {TEMPLATE_SECTIONS.map((section) => (
                <ListboxSection key={section.section} header={section.section}>
                    {section.templates
                        .filter(
                            (template) =>
                                !allowedTemplates ||
                                allowedTemplates.includes(template.id),
                        )
                        .map((template) => (
                            <ListboxItem
                                key={template.id}
                                value={template.id}
                                startContent={
                                    <img
                                        src={template.icon}
                                        className={styles.templateIcon}
                                        width={90}
                                        height={90}
                                        alt={
                                            TEMPLATE_DISPLAY_NAMES[template.id]
                                        }
                                    />
                                }
                                className={styles.listboxItem}
                            >
                                <div>
                                    <p className={styles.templateTitle}>
                                        {TEMPLATE_DISPLAY_NAMES[template.id]}
                                    </p>
                                    <p className={styles.templateDescription}>
                                        {template.description}
                                    </p>
                                </div>
                            </ListboxItem>
                        ))}
                </ListboxSection>
            ))}
        </Listbox>
    );
};
